import React, { useEffect, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { fetchQuickBitesMenu, BubbleTeaMenuItemResponse, InstantCakeItemResponse, QuickBitesMenuResponse } from '../api/bubbletea';
import { productsAPI } from '../api/products';
import { ProductDetail } from '../types/product';
import { getLocalizedField } from '../utils/localize';
import type { SupportedLanguage } from '../i18n';

const API_ORIGIN = (process.env.REACT_APP_API_URL || 'http://localhost:8000/api').replace('/api', '');
const resolveImageUrl = (url: string | null | undefined): string =>
  !url ? '' : (url.startsWith('data:') || url.startsWith('http')) ? url : `${API_ORIGIN}${url}`;

// ── Variation B colour tokens ──────────────────────────────────────────────
const HERO_BG       = '#1C1511';
const PAGE_BG       = '#FAF8F3';
const ACCENT        = '#D4A017';
const CARD_BG       = '#F5F0E8';
const TEXT_PRIMARY  = '#1C1511';
const TEXT_LIGHT    = '#FFFFFF';
const TEXT_MUTED    = '#6B5E4E';
const BORDER        = '#E8DDD0';

const SERIF_FONT = "Georgia, 'Times New Roman', serif";
const SANS_FONT  = "'Inter', -apple-system, BlinkMacSystemFont, sans-serif";

// ── Hero ───────────────────────────────────────────────────────────────────
const HeroSection = styled.section`
  background: ${HERO_BG};
  color: ${TEXT_LIGHT};
  padding: 5rem 1rem 4rem;
  margin-top: var(--header-height);
  text-align: center;

  @media (min-width: 768px) {
    padding: 7rem 2rem 5rem;
  }
`;

const HeroTitle = styled.h1`
  font-family: ${SERIF_FONT};
  font-size: clamp(2.2rem, 5vw, 3.8rem);
  font-weight: 700;
  color: ${TEXT_LIGHT};
  margin-bottom: 1rem;
  letter-spacing: 0.02em;
  line-height: 1.2;
`;

const HeroSubtitle = styled.p`
  font-family: ${SANS_FONT};
  font-size: clamp(1rem, 2.5vw, 1.35rem);
  color: ${PAGE_BG};
  margin-bottom: 2.5rem;
  opacity: 0.92;
  line-height: 1.6;
`;

const OrderNowLabel = styled.h2`
  font-family: ${SANS_FONT};
  font-size: clamp(0.8rem, 2vw, 1rem);
  font-weight: 600;
  margin-bottom: 1rem;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: ${ACCENT};
`;

const CTAButton = styled(Link)`
  display: inline-block;
  background-color: ${ACCENT};
  color: ${TEXT_PRIMARY};
  padding: 0.9rem 2rem;
  border-radius: 4px;
  font-family: ${SANS_FONT};
  font-weight: 700;
  font-size: 1rem;
  text-decoration: none;
  letter-spacing: 0.03em;
  transition: transform 0.25s, box-shadow 0.25s, background-color 0.25s;

  &:hover {
    background-color: #c49010;
    transform: translateY(-2px);
    box-shadow: 0 6px 18px rgba(212, 160, 23, 0.35);
  }
`;

const CTAGroup = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: center;
  flex-wrap: wrap;
`;

// ── Generic content section ────────────────────────────────────────────────
const Section = styled.section<{ bg?: string }>`
  background: ${({ bg }) => bg ?? PAGE_BG};
  padding: 3.5rem 0;

  @media (min-width: 768px) {
    padding: 4.5rem 0;
  }
`;

const SectionTitle = styled.h2`
  font-family: ${SERIF_FONT};
  text-align: center;
  color: ${TEXT_PRIMARY};
  font-size: clamp(1.6rem, 3.5vw, 2.4rem);
  font-weight: 700;
  margin-bottom: 2rem;
  letter-spacing: 0.01em;
`;

const SubSectionTitle = styled.h3`
  font-family: ${SERIF_FONT};
  color: ${TEXT_PRIMARY};
  font-size: clamp(1.1rem, 2.5vw, 1.5rem);
  font-weight: 600;
  margin: 2rem 0 1.25rem;
  text-align: center;
`;

// ── Product grid ───────────────────────────────────────────────────────────
const ItemGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.5rem;

  @media (min-width: 1024px) {
    grid-template-columns: repeat(4, 1fr);
  }
`;

const ItemCard = styled.div`
  background: ${CARD_BG};
  border-radius: 6px;
  border: 1px solid ${BORDER};
  box-shadow: 0 2px 10px rgba(28, 21, 17, 0.08);
  overflow: hidden;
  display: block;
  text-decoration: none;
  color: inherit;
  transition: transform 0.2s, box-shadow 0.2s;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 6px 20px rgba(28, 21, 17, 0.14);
  }
`;

const ItemImage = styled.img`
  width: 100%;
  aspect-ratio: 4 / 3;
  object-fit: cover;
  background: ${BORDER};
`;

const ItemInfo = styled.div`
  padding: 0.85rem 1rem;
`;

const ItemName = styled.p`
  font-family: ${SANS_FONT};
  font-weight: 600;
  font-size: 0.92rem;
  color: ${TEXT_PRIMARY};
  margin-bottom: 0.3rem;
`;

const ItemPrice = styled.p`
  font-family: ${SANS_FONT};
  color: ${ACCENT};
  font-weight: 700;
  font-size: 0.9rem;
`;

const EmptySubSection = styled.div`
  text-align: center;
  font-family: ${SANS_FONT};
  color: ${TEXT_MUTED};
  padding: 2rem 1rem;
`;

// ── Skeleton loader ────────────────────────────────────────────────────────
const shimmer = keyframes`
  0%   { background-position: -400px 0; }
  100% { background-position:  400px 0; }
`;

const SkeletonCard = styled.div`
  height: 200px;
  border-radius: 6px;
  background: linear-gradient(90deg, ${BORDER} 25%, #f0e8dc 50%, ${BORDER} 75%);
  background-size: 800px 100%;
  animation: ${shimmer} 1.4s infinite linear;
`;

// ── Features / "Why choose us" ─────────────────────────────────────────────
const Features = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5rem;
  margin-top: 1rem;

  @media (min-width: 768px) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

const FeatureCard = styled.div`
  text-align: center;
  padding: 2rem 1.5rem;
  background: ${CARD_BG};
  border-radius: 6px;
  border: 1px solid ${BORDER};
  box-shadow: 0 2px 8px rgba(28, 21, 17, 0.07);
`;

const FeatureIcon = styled.div`
  font-size: 2.75rem;
  margin-bottom: 1rem;
`;

const FeatureTitle = styled.h3`
  font-family: ${SERIF_FONT};
  color: ${TEXT_PRIMARY};
  font-size: 1.1rem;
  font-weight: 700;
  margin-bottom: 0.75rem;
`;

const FeatureDescription = styled.p`
  font-family: ${SANS_FONT};
  color: ${TEXT_MUTED};
  line-height: 1.65;
  font-size: 0.93rem;
`;

// ── Accent divider line ────────────────────────────────────────────────────
const AccentDivider = styled.div`
  width: 56px;
  height: 3px;
  background: ${ACCENT};
  margin: 0 auto 2.5rem;
  border-radius: 2px;
`;

// ── Page component ─────────────────────────────────────────────────────────
const HomePage: React.FC = () => {
  const { t, i18n } = useTranslation();
  // Mirrors the LanguageToggle.tsx / QuickBitesMenu.tsx pattern: i18next's
  // language can be a region-qualified tag or unset, so narrow to the two
  // v1-supported values.
  const language: SupportedLanguage = (i18n.language as SupportedLanguage) === 'zh' ? 'zh' : 'en';

  const [qbMenu, setQbMenu] = useState<QuickBitesMenuResponse | null>(null);
  const [qbLoading, setQbLoading] = useState(true);
  const [ccProducts, setCcProducts] = useState<ProductDetail[]>([]);
  const [ccLoading, setCcLoading] = useState(true);

  useEffect(() => {
    // task-030-ci-fix: guard against firing state updates / console.error once
    // this component has unmounted. Without this, a slow-to-settle fetch that
    // resolves/rejects after unmount (e.g. a test that renders HomePage and
    // moves on before this promise settles) logs via console.error AFTER Jest
    // has already torn the test down, producing "Cannot log after tests done"
    // -- which fails the whole `npm test -- --ci` process even though every
    // individual test still reports as passed (see .orchestrator/learnings.md
    // task-030 "Test Backend fails unconditionally" entry for the CI-wide
    // symptom this was one cause of).
    let cancelled = false;
    fetchQuickBitesMenu()
      .then(data => { if (!cancelled) setQbMenu(data); })
      .catch(err => { if (!cancelled) console.error('Failed to load QB menu:', err); })
      .finally(() => { if (!cancelled) setQbLoading(false); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    // Same unmount guard as the QB-menu effect above.
    let cancelled = false;
    productsAPI.getProducts()
      .then(data => { if (!cancelled) setCcProducts(Array.isArray(data) ? data : []); })
      .catch(err => { if (!cancelled) console.error('Failed to load CC products:', err); })
      .finally(() => { if (!cancelled) setCcLoading(false); });
    return () => { cancelled = true; };
  }, []);

  return (
    <>
      {/* ── Dark premium hero ── */}
      <HeroSection>
        <div className="container">
          <HeroTitle>{t('home.heroTitle')}</HeroTitle>
          <HeroSubtitle>
            {t('home.heroSubtitle')}
          </HeroSubtitle>
          <OrderNowLabel>{t('home.orderNowLabel')}</OrderNowLabel>
          <CTAGroup>
            <CTAButton to="/quick-bites">{t('home.ctaQuickBites')}</CTAButton>
            <CTAButton to="/custom-cakes">{t('home.ctaCustomCakes')}</CTAButton>
          </CTAGroup>
        </div>
      </HeroSection>

      {/* ── Why choose us ── */}
      <Section bg={PAGE_BG}>
        <div className="container">
          <SectionTitle>{t('home.whyChooseUsTitle')}</SectionTitle>
          <AccentDivider />
          <Features>
            <FeatureCard>
              <FeatureIcon>🎂</FeatureIcon>
              <FeatureTitle>{t('home.featureFreshDailyTitle')}</FeatureTitle>
              <FeatureDescription>
                {t('home.featureFreshDailyDescription')}
              </FeatureDescription>
            </FeatureCard>

            <FeatureCard>
              <FeatureIcon>🚚</FeatureIcon>
              <FeatureTitle>{t('home.featureEasyPickupTitle')}</FeatureTitle>
              <FeatureDescription>
                {t('home.featureEasyPickupDescription')}
              </FeatureDescription>
            </FeatureCard>

            <FeatureCard>
              <FeatureIcon>💳</FeatureIcon>
              <FeatureTitle>{t('home.featureSecurePaymentTitle')}</FeatureTitle>
              <FeatureDescription>
                {t('home.featureSecurePaymentDescription')}
              </FeatureDescription>
            </FeatureCard>
          </Features>
        </div>
      </Section>

      {/* ── Popular products ── */}
      <Section bg={PAGE_BG}>
        <div className="container">
          <SectionTitle>{t('home.popularProductsTitle')}</SectionTitle>
          <AccentDivider />

          {/* QB Sub-section */}
          <SubSectionTitle>{t('home.qbSubSectionTitle')}</SubSectionTitle>
          {qbLoading ? (
            <ItemGrid>{[...Array(4)].map((_, i) => <SkeletonCard key={i} />)}</ItemGrid>
          ) : (() => {
            const bubbleTeas = Array.isArray(qbMenu?.bubble_tea) ? qbMenu!.bubble_tea.slice(0, 4) : [];
            const instantCakes = Array.isArray(qbMenu?.instant_cakes) ? qbMenu!.instant_cakes.slice(0, 4) : [];
            const allQB = [...bubbleTeas, ...instantCakes];
            if (allQB.length === 0) return <EmptySubSection>{t('home.qbEmptyState')}</EmptySubSection>;
            return (
              <ItemGrid>
                {allQB.map((item, i) => {
                  const cat = 'menu_item_id' in item
                    ? (item as BubbleTeaMenuItemResponse).menu_category
                    : (item as InstantCakeItemResponse).category;
                  const qbSlug = cat
                    ? '/quick-bites?category=' + cat.toLowerCase().replace(/\s+/g, '-')
                    : '/quick-bites';
                  return (
                    <ItemCard as={Link} to={qbSlug} key={i}>
                      <ItemImage
                        src={resolveImageUrl(item.image_url) || 'https://via.placeholder.com/400x300?text=Quick+Bites'}
                        alt={getLocalizedField(item, 'name', language) as string}
                      />
                      <ItemInfo>
                        <ItemName>{getLocalizedField(item, 'name', language) as string}</ItemName>
                        <ItemPrice>£{Number(item.price).toFixed(2)}</ItemPrice>
                      </ItemInfo>
                    </ItemCard>
                  );
                })}
              </ItemGrid>
            );
          })()}

          {/* CC Sub-section */}
          <SubSectionTitle>{t('home.ccSubSectionTitle')}</SubSectionTitle>
          {ccLoading ? (
            <ItemGrid>{[...Array(4)].map((_, i) => <SkeletonCard key={`cc-${i}`} />)}</ItemGrid>
          ) : ccProducts.length === 0 ? (
            <EmptySubSection>{t('home.ccEmptyState')}</EmptySubSection>
          ) : (
            <ItemGrid>
              {ccProducts.slice(0, 4).map(product => (
                <ItemCard as={Link} to={`/products/${product.product_id}`} key={product.product_id}>
                  <ItemImage
                    src={
                      Array.isArray(product.images) && product.images.length > 0
                        ? (product.images.find(img => img.is_primary)?.image_url || product.images[0].image_url)
                        : 'https://via.placeholder.com/400x300?text=Custom+Cake'
                    }
                    alt={getLocalizedField(product, 'name', language) as string}
                  />
                  <ItemInfo>
                    <ItemName>{getLocalizedField(product, 'name', language) as string}</ItemName>
                    <ItemPrice>£{Number(product.base_price).toFixed(2)}</ItemPrice>
                  </ItemInfo>
                </ItemCard>
              ))}
            </ItemGrid>
          )}
        </div>
      </Section>
    </>
  );
};

export default HomePage;
